// Default state definition
const defaultState = () => {
  return {
    // Cache for formatted values to avoid recalculating
    cachedValues: {
      formattedMaxStorage: null,
      formattedUsedStorage: null,
      storagePercentage: null,
      memberPercentage: null
    }
  };
};

const resources = {
  namespaced: true,
  state: defaultState(),
  mutations: {
    resetState(state) {
      Object.assign(state, defaultState());
    },
    updateCachedValue(state, { key, value }) {
      state.cachedValues[key] = value;
    }
  },
  actions: {
    resetState({ commit }) {
      commit('resetState');
    },
    // Cache a formatted value to avoid recalculation
    cacheValue({ commit }, { key, value }) {
      commit('updateCachedValue', { key, value });
    }
  },
  getters: {
    // Max storage in bytes
    maxStorageBytes: (state, getters, rootState, rootGetters) => {
      const user = rootGetters['user/getUser'] || {};
      return user?.plan?.plans?.max_storage_size || 0;
    },

    // Used storage in bytes
    usedStorageBytes: (state, getters, rootState, rootGetters) => {
      const user = rootGetters['user/getUser'] || {};
      return user?.plan?.total_storage_size || 0;
    },

    // Formatted max storage (e.g., "20 GB")
    formattedMaxStorage: (state, getters) => {
      if (state.cachedValues.formattedMaxStorage) {
        return state.cachedValues.formattedMaxStorage;
      }
      return formatBytes(getters.maxStorageBytes);
    },

    // Formatted used storage (e.g., "15 GB")
    formattedUsedStorage: (state, getters) => {
      if (state.cachedValues.formattedUsedStorage) {
        return state.cachedValues.formattedUsedStorage;
      }
      return formatBytes(getters.usedStorageBytes);
    },

    // Storage usage percentage
    storagePercentage: (state, getters) => {
      if (state.cachedValues.storagePercentage !== null) {
        return state.cachedValues.storagePercentage;
      }

      if (getters.maxStorageBytes === 0) return 0;
      const percentage = (getters.usedStorageBytes / getters.maxStorageBytes) * 100;
      return Math.min(100, Math.round(percentage));
    },

    // Storage bar color based on usage
    storageBarColor: (state, getters) => {
      if (getters.storagePercentage >= 90) return 'var(--negative)'; // Use theme variable for red
      if (getters.storagePercentage >= 75) return 'var(--warning)'; // Use theme variable for orange/warning
      return 'var(--positive)'; // Use theme variable for green/positive
    },

    // Maximum number of team members
    maxMembers: (state, getters, rootState, rootGetters) => {
      const user = rootGetters['user/getUser'] || {};
      return user?.plan?.plans?.max_members || 0;
    },

    // Current number of team members
    totalMembers: (state, getters, rootState, rootGetters) => {
      const user = rootGetters['user/getUser'] || {};
      return user?.plan?.total_members || 0;
    },

    // Display count for members (handles infinite case)
    memberCount: (state, getters) => {
      return getters.maxMembers === null ? '∞' : getters.maxMembers;
    },

    // Team member usage percentage
    memberPercentage: (state, getters) => {
      if (state.cachedValues.memberPercentage !== null) {
        return state.cachedValues.memberPercentage;
      }

      if (getters.maxMembers === 0 || getters.maxMembers === null) return 0;
      const percentage = (getters.totalMembers / getters.maxMembers) * 100;
      return Math.min(100, Math.round(percentage));
    },

    // Member indicator color based on usage
    memberBarColor: (state, getters) => {
      if (getters.memberPercentage >= 90) return 'negative'; // Use Quasar color name (maps to theme variable)
      if (getters.memberPercentage >= 75) return 'warning'; // Use Quasar color name (maps to theme variable)
      return 'positive'; // Use Quasar color name (maps to theme variable)
    },

    // Formatter function for bytes (for external use)
    formattedBytes:
      () =>
      (bytes, decimals = 0) => {
        return formatBytes(bytes, decimals);
      }
  }
};

/**
 * Format bytes to human-readable format
 * @param {number} bytes - Number of bytes to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} - Formatted string (e.g., "1.5 GB")
 */
function formatBytes(bytes, decimals = 0) {
  if (!bytes || bytes === 0) return '0 Bytes';

  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const unitDivisor = 1024;
  const unitIndex = Math.floor(Math.log(bytes) / Math.log(unitDivisor));
  const value = (bytes / Math.pow(unitDivisor, unitIndex)).toFixed(decimals);

  return `${value} ${units[unitIndex]}`;
}

export default resources;
